import React, { useEffect, useState } from "react";
import { X, Search } from "lucide-react";
import { Spinner } from "./ui/kit";
import { fmtKc, fmtPct } from "../lib/format";
import api from "../lib/api";
import Vyber from "./Vyber";

/** Výběr položky z ceníku (editor nabídky, karta zařízení).
    `vyrobceId` předvybere výrobce (na kartě zařízení jeho výrobce); položky
    bez výrobce (práce, doprava) zůstávají vidět i s filtrem. */
export default function CenikPicker({ kategorieId, vyrobceId = "", onAdd, onClose }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [typ, setTyp] = useState(null);
  const [vyrobci, setVyrobci] = useState([]);
  const [vyrobce, setVyrobce] = useState(vyrobceId || "");
  // Subdodavatel dostane místo nákupu prodejní cenu (server, skryt_nakup).
  const [skryt, setSkryt] = useState(false);
  useEffect(() => {
    api.get("/api/polozky/pro-nabidku", { params: { q, kategorie_id: kategorieId, typ, vyrobce_id: vyrobce || undefined,
      bez_vyrobce: vyrobce ? true : undefined, limit: 100 } })
      .then((r) => { setItems(r.data.items); setVyrobci(r.data.vyrobci || []); setSkryt(!!r.data.skryt_nakup); })
      .catch(() => setItems([]));
  }, [q, typ, kategorieId, vyrobce]);
  const jmenoVyrobce = Object.fromEntries(vyrobci.map((v) => [v.id, v.name]));
  return (
    <div className="fixed inset-0 z-[60] flex md:items-center items-end justify-center md:p-4" data-testid="cenik-picker">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[560px] md:rounded-card rounded-t-sheet shadow-panel max-h-[80vh] flex flex-col">
        <div className="border-b border-line-softer px-5 py-4 flex items-center justify-between">
          <h2 className="text-[15px] font-800">Přidat z ceníku</h2>
          <button onClick={onClose} aria-label="Zavřít" className="tap text-muted hover:text-ink"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 field"><Search size={15} className="text-muted shrink-0" />
            <input className="bg-transparent outline-none w-full text-[16px] md:text-[13px]" placeholder="Hledat…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="picker-search" /></div>
          <div className="flex flex-wrap items-center gap-2">
            <Vyber className="field flex-1 min-w-[160px]" value={vyrobce} onChange={(e) => setVyrobce(e.target.value)}
              aria-label="Výrobce" data-testid="picker-vyrobce">
              <option value="">Všichni výrobci</option>
              {vyrobci.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Vyber>
            {["MATERIAL", "PRACE", "DOPRAVA"].map((t) => (
              <button key={t} onClick={() => setTyp(typ === t ? null : t)} className={`tap md:min-h-0 pill px-2.5 h-8 text-[11px] font-600 border ${typ === t ? "bg-accent text-white border-accent" : "bg-white text-sec border-line-strong"}`}>{t[0] + t.slice(1).toLowerCase()}</button>
            ))}
          </div>
          {vyrobce && <p className="t-meta text-muted">Jen {jmenoVyrobce[vyrobce] || "vybraný výrobce"} a položky bez výrobce (práce, doprava).</p>}
        </div>
        <div className="overflow-y-auto flex-1">
          {items === null ? <div className="p-8 flex justify-center"><Spinner /></div>
            : !items.length ? <p className="px-5 py-6 t-meta text-muted">Nic jsme nenašli.</p>
            : items.map((p) => (
              <button key={p.id} onClick={() => onAdd(p)} data-testid={`picker-item-${p.id}`}
                className="w-full text-left flex items-center gap-3 px-5 py-2.5 border-t border-line-softer hover:bg-inputbg">
                <span className="min-w-0 flex-1"><span className="block text-[12.5px] font-600 truncate">{p.name}</span>
                  <span className="text-[11px] text-muted">{[jmenoVyrobce[p.vyrobce_id], p.mj, !skryt && p.cena_prodej == null && `marže ${fmtPct(p.vychozi_marze_pct)} %`, `DPH ${p.vychozi_dph_pct ?? 21}%`].filter(Boolean).join(" · ")}</span></span>
                {/* Subdodavatel vidí prodejní cenu v `cena_nakup`; jinak nákup (může být neznámý) a oficiální prodej. */}
                <span className="amount text-[12px] text-sec text-right shrink-0" title={skryt ? "Prodejní cena bez DPH" : "Nákupní cena"}>
                  {p.cena_nakup == null ? "nákup —" : `${fmtKc(p.cena_nakup)} Kč`}
                  {!skryt && p.cena_prodej != null && <span className="block text-[10.5px] text-muted" title="Oficiální prodejní cena">prodej {fmtKc(p.cena_prodej)} Kč</span>}
                </span>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}
